import { goto } from '$app/navigation';
import { openPostInModal, postInModal, postModalTabs } from '$stores';
import { createDonation } from '$utils/api/donation.util';
import { appreciate, deletePost, findPostById } from '$utils/api/posts.util';
import type { IUser, FPost, WithCreatorData, IComment } from 'garden-common';
import { postComment, postCommentReply } from './api/comments.util';
import { get } from 'svelte/store';
import { isMobile } from '$src/stores/media.store';

export type modulesType =
    | 'create-gallery'
    | 'donation-post'
    | 'delete-post'
    | 'add-to-gallery'
    | 'save-to-gallery'
    | null;

export class PostsCore<TUser extends IUser<string>> {
    openModal: modulesType = null;

    isThreeDotOpened: boolean = false;
    post: Partial<FPost<string>> & Partial<WithCreatorData>;

    constructor(
        private readonly me: TUser,
        post: Partial<FPost<string>> & Partial<WithCreatorData>
    ) {
        this.post = post;
    }

    getPost() {
        return this.post;
    }

    onDonate() {
        if (!this.me) {
            openPostInModal.set(false);
            setTimeout(() => goto('/login'), 100);
            return;
        }
        this.openModal = 'donation-post';
    }

    async onAppreciate() {
        try {
            if (!this.me) {
                openPostInModal.set(false);
                setTimeout(() => goto('/login'), 100);
                return;
            }
            if (!this.post.likes?.includes(this.me._id)) {
                this.post.likes?.push(this.me._id);
            } else {
                const i = this.post.likes.findIndex(id => id === this.me._id);
                this.post.likes.splice(i, 1);
            }
            this.post.likes = this.post.likes;

            await appreciate(this.post._id.toString());
        } catch (e) {
            throw new Error(`[Appreciate] Post core: ${e}`);
        }
    }

    onComment() {
        if (!this.me) {
            openPostInModal.set(false);
            setTimeout(() => goto('/login'), 100);
            return;
        }
        this.onPreviewPost();
        postModalTabs.set('Comments');
    }

    onSave() {
        if (!this.me) {
            openPostInModal.set(false);
            setTimeout(() => goto('/login'), 100);
            return;
        }
        this.openModal = 'save-to-gallery';
    }

    async onPreviewPost() {
        if (get(isMobile)) {
            goto(`/post/${this.post._id}`);
            return;
        }

        goto(`?post=${this.post._id}`);

        openPostInModal.set(true);

        postInModal.set({ post: this.post, isLoading: false });

        try {
            await findPostById(this.post._id, 'details').then(res => {
                this.post.tags = res.tags;
                this.post.collaborators = res.collaborators;
                this.post.classic = res.classic;

                for (let i = 0; i < this.post.media.length; i++) {
                    delete this.post.media[i].details;
                    this.post.media[i]['details'] = res.media[i].details;
                }

                postInModal.set({ post: this.post, isLoading: false });
            });
        } catch (e) {
            throw new Error(`[Fetch preview] Post core: ${e}`);
        }
    }

    async onFullPost() {
        try {
            openPostInModal.set(true);

            goto(`?post=${this.post._id}`);

            postInModal.set({ post: this.post, isLoading: false });

            await findPostById(this.post._id, 'full').then(res => {
                for (let i = 0; i < this.post.media.length; i++) {
                    delete this.post.media[i].details;
                    this.post.media[i]['details'] = res.media[i].details;
                }

                this.post.tags = res.tags;
                this.post.creatorData = res.creatorData;

                postInModal.set({ post: this.post, isLoading: false });
            });
        } catch (e) {
            throw new Error(`[Fetch full] Post core: ${e}`);
        }
    }

    // Dot menu callbacks
    onReportPost() {
        this.isThreeDotOpened = false;
    }

    onCopyLink() {
        const url = new URL(`/post/${this.post._id}`, window.location.href).toString();
        navigator.clipboard.writeText(url);
        try {
            if (navigator.share) {
                navigator
                    .share({
                        title: "The Garden",
                        text: "Share this post!",
                        url
                    })
                    .then(() => console.log('Successful share! 🎉'))
                    .catch(err => console.error(err));
            } else {
                console.log('Web Share API is not supported in this browser.');
            }
            this.isThreeDotOpened = false;
        } catch (error) {
            console.error('Error sharing: ', error);
        }

    }

    onViewProfile() {
        if (!this.post.creatorData?.userName) return;
        goto('/profile/' + this.post.creatorData?.userName);
    }

    async Donation(e: CustomEvent) {
        try {
            this.post.totalDonationsSeed += Math.round(parseInt(e.detail, 10));

            await createDonation(this.post._id, Math.round(parseInt(e.detail, 10)));
        } catch (error) {
            throw new Error(`[Dontaion] Post core: ${e}`);
        }
    }

    async CommentReply(e: {
        comment: {
            commentText: IComment<string>['commentText'];
            taggedUser: IComment<string>['taggedUser'];
        };
        parentComment: IComment<string>['_id'];
        donatedSeeds: number;
        tagDetect: boolean;
        taggedUser: string[];
    }) {
        try {
            this.post.totalDonationsSeed += e.donatedSeeds;
            this.post.comments.length++;
            return await postCommentReply(
                this.post._id,
                e.parentComment,
                e.comment.commentText,
                Math.round(e.donatedSeeds),
                e.comment.taggedUser
            );
        } catch (error) {
            throw new Error(`[CommentReply] Post core: ${JSON.stringify(e)}`);
        }
    }

    async Comment(e: {
        comment: {
            commentText: IComment<string>['commentText'];
            taggedUser: IComment<string>['taggedUser'];
        };
        donatedSeeds: number;
        tagDetect: boolean;
        taggedUser: string[];
    }) {
        try {
            this.post.totalDonationsSeed += e.donatedSeeds;
            this.post.comments.length++;
            console.log(this.post._id);

            return await postComment(
                this.post._id,
                e.comment.commentText,
                Math.round(e.donatedSeeds),
                e.comment.taggedUser
            );
        } catch (error) {
            throw new Error(`[Comment] Post core: ${JSON.stringify(e)}`);
        }
    }

    OpenDeletethisPost() {
        this.openModal = 'delete-post';
    }

    SavePostToGallery() {
        if (!this.post.savers.includes(this.me._id)) {
            this.post.savers.push(this.me._id);
        }
        this.post.savers = this.post.savers;
    }

    OpenCreateGallery() {
        this.openModal = 'create-gallery';
    }

    CloseCreateGallery() {
        return (this.openModal = 'save-to-gallery' as modulesType);
    }

    Close() {
        return (this.openModal = null);
    }

    async DeletePost() {
        try {
            await deletePost(this.post._id.toString());
        } catch (e) {
            throw new Error(`[Delete] Post core: ${e}`);
        }
    }
}
